import { useEffect, useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api, { getToken } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PortalSwitcher from "@/components/PortalSwitcher";
import { MessagesSection } from "@/components/MessageThread";
import { CrmImportPanel } from "@/components/CrmImportPanel";
import { CaseDocSyncBanner } from "@/components/EnterpriseCrm";
import { DocumentRequests } from "@/components/DocumentRequests";
import RetainerIntelWidget from "@/components/RetainerIntelWidget";
import { PremiumEvidenceWidget, EvidenceIntelBadge } from "@/components/EvidenceIntelligence";
import CrashReadyVoice from "@/components/CrashReadyVoice";
import {
  Scale, LogOut, FileText, Clock, Camera, FolderDown,
  Check, X, ChevronRight, Loader2, Inbox, Search, Bell, ReceiptText, FileDown, Flame, CreditCard, Zap, HeartPulse,
} from "lucide-react";
import { PriorityReservations } from "@/components/PriorityReservations";
import AutoAcceptPrefs from "@/components/AutoAcceptPrefs";
import AttorneyTelehealth from "@/components/AttorneyTelehealth";
import FirmCommandCenter from "@/components/firm/FirmCommandCenter";
import CommandBar from "@/components/firm/CommandBar";
import QuickActions from "@/components/firm/QuickActions";
import { LayoutDashboard, FolderOpen } from "lucide-react";

const STATUS = {
  requested: { label: "New Referral", cls: "bg-amber-50 text-amber-600" },
  accepted: { label: "Accepted", cls: "bg-green-50 text-green-600" },
  declined: { label: "Declined", cls: "bg-slate-100 text-slate-500" },
};

export default function AttorneyPortal() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [cases, setCases] = useState([]);
  const [active, setActive] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const [notifs, setNotifs] = useState([]);
  const [unread, setUnread] = useState(0);
  const [showNotif, setShowNotif] = useState(false);
  const [showInvoices, setShowInvoices] = useState(false);
  const [showAutoAccept, setShowAutoAccept] = useState(false);
  const [showTelehealth, setShowTelehealth] = useState(false);
  const [invoices, setInvoices] = useState([]);
  const [portalView, setPortalView] = useState("command");
  const [fccView, setFccView] = useState("home");
  const [cmdOpen, setCmdOpen] = useState(false);
  const token = getToken();

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") { e.preventDefault(); setCmdOpen((v) => !v); }
      if (e.key === "Escape") setCmdOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const handleNav = (nav) => {
    if (typeof nav === "string") nav = { action: nav };
    if (nav.result) {
      const r = nav.result;
      if (r.type === "case") { setPortalView("cases"); openCase(r.id); }
      else if (r.type === "export") { setPortalView("command"); setFccView("litigation"); }
      else { setShowTelehealth(true); }
      return;
    }
    if (nav.action === "cases") { setPortalView("cases"); if (nav.caseId) openCase(nav.caseId); }
    else if (nav.action === "telehealth") setShowTelehealth(true);
    else if (nav.action === "autoaccept") setShowAutoAccept(true);
    else if (nav.action === "billing") navigate("/attorney/billing");
    else if (nav.view) { setPortalView("command"); setFccView(nav.view); }
  };

  const loadExtras = async () => {
    try {
      const [n, inv] = await Promise.all([api.get("/notifications"), api.get("/attorney/invoices")]);
      setNotifs(n.data.items); setUnread(n.data.unread); setInvoices(inv.data);
    } catch { /* ignore */ }
  };
  useEffect(() => { loadExtras(); const t = setInterval(loadExtras, 30000); return () => clearInterval(t); }, []);

  const openNotifs = async () => {
    setShowNotif((v) => !v);
    if (!showNotif && unread > 0) {
      try { await api.post("/notifications/read-all"); setUnread(0); } catch { /* ignore */ }
    }
  };

  const outstanding = invoices.filter((i) => i.status === "unpaid").reduce((s, i) => s + (i.amount || 0), 0);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/attorney/referrals"); setCases(data); }
    catch { toast.error("Could not load referrals"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openCase = async (caseId) => {
    try {
      const { data } = await api.get(`/cases/${caseId}`);
      setActive(data);
      // Bring the case detail into view (critical on mobile + when the dashboard is tall).
      setTimeout(() => document.getElementById("attorney-case-detail")?.scrollIntoView({ behavior: "smooth", block: "start" }), 250);
    }
    catch { toast.error("Could not open case"); }
  };

  // Deep-link from the claim success screen: /attorney?case=<id>&focus=crm|chat
  const [searchParams] = useSearchParams();
  useEffect(() => {
    const cid = searchParams.get("case");
    if (cid) { setPortalView("cases"); openCase(cid); }
    // eslint-disable-next-line
  }, [searchParams]);
  useEffect(() => {
    if (!active) return;
    const focus = searchParams.get("focus");
    if (!focus) return;
    const id = focus === "chat" ? "messages-panel" : focus === "crm" ? "crm-panel" : null;
    if (id) setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" }), 300);
    // eslint-disable-next-line
  }, [active]);

  const act = async (caseId, action) => {
    try {
      await api.post(`/attorney/cases/${caseId}/action`, { action });
      toast.success(`Case ${action}`);
      load();
      if (active?.case_id === caseId) setActive({ ...active, referral_status: action });
    } catch { toast.error("Action failed"); }
  };

  const doLogout = async () => { await logout(); navigate("/"); };

  const FILTERS = [
    { key: "all", label: "All" },
    { key: "requested", label: "New" },
    { key: "accepted", label: "Accepted" },
    { key: "declined", label: "Declined" },
  ];
  const term = q.trim().toLowerCase();
  const filtered = cases.filter((c) => {
    if (filter !== "all" && c.referral_status !== filter) return false;
    if (!term) return true;
    return [c.client_name, c.client_email, c.title]
      .filter(Boolean)
      .some((v) => v.toLowerCase().includes(term));
  });

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/80 border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <Link to="/attorney" className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-xl bg-navy flex items-center justify-center"><Scale className="w-5 h-5 text-white" strokeWidth={2.2} /></div>
            <span className="hidden lg:inline font-heading font-extrabold text-lg tracking-tight text-navy whitespace-nowrap">Crash Ready <span className="text-cobalt">Attorney</span></span>
          </Link>
          <div className="flex items-center gap-2">
            <PortalSwitcher />
            <div className="hidden md:flex items-center rounded-full bg-slate-100 p-0.5">
              <button onClick={() => setPortalView("command")} data-testid="view-command"
                className={`flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-semibold transition-all ${portalView === "command" ? "bg-navy text-white" : "text-slate-500"}`}>
                <LayoutDashboard className="w-3.5 h-3.5" /> Command
              </button>
              <button onClick={() => setPortalView("cases")} data-testid="view-cases"
                className={`flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-semibold transition-all ${portalView === "cases" ? "bg-navy text-white" : "text-slate-500"}`}>
                <FolderOpen className="w-3.5 h-3.5" /> Cases
              </button>
            </div>
            <button onClick={() => setCmdOpen(true)} data-testid="open-command-bar"
              className="hidden sm:flex items-center gap-2 px-3.5 py-2 rounded-full text-xs font-medium text-slate-500 border border-slate-200 hover:border-cobalt hover:text-cobalt transition-all">
              <Search className="w-3.5 h-3.5" /> Search <kbd className="text-[9px] bg-slate-100 rounded px-1">⌘K</kbd>
            </button>
            <button onClick={() => setShowAutoAccept(true)} data-testid="nav-auto-accept"
              className="hidden sm:flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium text-slate-600 hover:bg-amber-50 hover:text-amber-600 transition-all">
              <Zap className="w-4 h-4" /> Auto-Accept
            </button>
            <button onClick={() => setShowTelehealth(true)} data-testid="nav-telehealth"
              className="hidden sm:flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium text-slate-600 hover:bg-cobalt-light hover:text-cobalt transition-all">
              <HeartPulse className="w-4 h-4" /> Telehealth
            </button>
            <button onClick={() => navigate("/attorney/billing")} data-testid="nav-billing"
              className="hidden sm:flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium text-slate-600 hover:bg-cobalt-light hover:text-cobalt transition-all">
              <CreditCard className="w-4 h-4" /> Billing
            </button>
            <div className="relative">
              <button onClick={openNotifs} data-testid="attorney-bell" className="relative p-2 rounded-full hover:bg-slate-100 transition-all">
                <Bell className="w-5 h-5 text-slate-600" />
                {unread > 0 && <span className="absolute -top-0.5 -right-0.5 h-4 min-w-4 px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center">{unread}</span>}
              </button>
              {showNotif && (
                <div className="absolute right-0 mt-2 w-80 bg-white rounded-2xl border border-slate-100 shadow-xl overflow-hidden z-50">
                  <div className="px-4 py-3 border-b border-slate-100 font-heading font-semibold text-navy text-sm">Notifications</div>
                  <div className="max-h-80 overflow-y-auto">
                    {notifs.length === 0 ? <div className="p-6 text-center text-slate-400 text-sm">No notifications</div> :
                      notifs.slice(0, 20).map((n) => {
                        const hot = n.type === "retainer_hot";
                        return (
                          <div key={n.notification_id} data-testid={hot ? "notif-hot-lead" : undefined} className={`px-4 py-3 border-b border-slate-50 ${hot ? "bg-red-50 border-l-4 border-l-red-500" : n.read ? "" : "bg-cobalt-light/40"}`}>
                            <div className={`text-sm font-medium flex items-center gap-1.5 ${hot ? "text-red-600" : "text-navy"}`}>{hot && <Flame className="w-4 h-4 text-red-500" />}{n.title}</div>
                            {n.body && <div className="text-xs text-slate-500 mt-0.5">{n.body}</div>}
                            <div className="text-[10px] text-slate-400 mt-1">{new Date(n.created_at).toLocaleString()}</div>
                          </div>
                        );
                      })}
                  </div>
                </div>
              )}
            </div>
            <button onClick={doLogout} data-testid="attorney-logout" className="flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium text-slate-600 hover:bg-red-50 hover:text-red-600 transition-all"><LogOut className="w-4 h-4" /> Logout</button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        {portalView === "command" && (
          <FirmCommandCenter view={fccView} setView={setFccView} onAction={handleNav} />
        )}
        {portalView === "cases" && (<>
        <p className="text-xs font-medium text-slate-500 uppercase tracking-widest">Referrals</p>
        <h1 className="font-heading text-3xl md:text-4xl font-extrabold text-navy mt-1">Welcome, {user?.name?.split(" ")[0]}</h1>
        <p className="mt-2 text-slate-600">Review referred cases, inspect evidence and timelines, then accept or decline.</p>

        <PriorityReservations />

        {invoices.length > 0 && (
          <div className="mt-5 bg-white rounded-2xl border border-slate-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)]" data-testid="invoice-banner">
            <button onClick={() => setShowInvoices((v) => !v)} className="w-full p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-3 text-left">
              <div className="flex items-center gap-3">
                <div className="h-11 w-11 rounded-xl bg-cobalt-light flex items-center justify-center"><ReceiptText className="w-5 h-5 text-cobalt" /></div>
                <div>
                  <div className="font-semibold text-navy">Referral Invoices</div>
                  <div className="text-xs text-slate-500">{invoices.length} total · {invoices.filter((i) => i.status === "unpaid").length} unpaid · tap to view</div>
                </div>
              </div>
              <div className="text-right">
                <div className="font-heading text-2xl font-extrabold text-navy">${outstanding.toLocaleString()}</div>
                <div className="text-xs font-medium text-amber-600 uppercase tracking-wide">Outstanding</div>
              </div>
            </button>
            {showInvoices && (
              <div className="border-t border-slate-100 divide-y divide-slate-50">
                {invoices.map((i) => (
                  <div key={i.invoice_id} className="px-5 py-3 flex items-center justify-between gap-3" data-testid={`attorney-invoice-${i.invoice_id}`}>
                    <div>
                      <div className="text-sm font-medium text-navy">${Number(i.amount).toLocaleString()} · {i.case_title || "Case"}</div>
                      <div className="text-xs text-slate-400">{new Date(i.created_at).toLocaleDateString()}</div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${i.status === "paid" ? "bg-green-50 text-green-600" : "bg-amber-50 text-amber-600"}`}>{i.status}</span>
                      <a href={`${process.env.REACT_APP_BACKEND_URL}/api/invoices/${i.invoice_id}/pdf?auth=${token}`} target="_blank" rel="noreferrer" data-testid={`attorney-invoice-pdf-${i.invoice_id}`} className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-slate-200 text-slate-700 text-xs font-medium hover:bg-slate-50 transition-all"><FileDown className="w-3.5 h-3.5" /> PDF</a>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        <RetainerIntelWidget />

        <div className="mt-6"><PremiumEvidenceWidget /></div>

        <div className="mt-8 grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-1 space-y-3">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="attorney-search" placeholder="Search by client, email or case…"
                className="w-full bg-white border border-slate-200 rounded-full pl-11 pr-4 py-3 text-sm focus:ring-2 focus:ring-cobalt/20 focus:border-cobalt outline-none" />
            </div>
            <div className="flex flex-wrap gap-2">
              {FILTERS.map((f) => {
                const count = f.key === "all" ? cases.length : cases.filter((c) => c.referral_status === f.key).length;
                return (
                  <button key={f.key} onClick={() => setFilter(f.key)} data-testid={`attorney-filter-${f.key}`}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${filter === f.key ? "bg-cobalt text-white border-cobalt" : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"}`}>
                    {f.label} ({count})
                  </button>
                );
              })}
            </div>
            {loading ? (
              <div className="flex justify-center py-10"><Loader2 className="w-7 h-7 text-cobalt animate-spin" /></div>
            ) : filtered.length === 0 ? (
              <div className="bg-white rounded-3xl border border-dashed border-slate-200 p-10 text-center">
                <Inbox className="w-10 h-10 text-slate-300 mx-auto" />
                <p className="mt-3 text-slate-500 text-sm">{cases.length === 0 ? "No referrals yet." : "No matching referrals."}</p>
              </div>
            ) : filtered.map((c) => {
              const st = STATUS[c.referral_status] || STATUS.requested;
              return (
                <button key={c.case_id} onClick={() => openCase(c.case_id)} data-testid={`referral-${c.case_id}`}
                  className={`w-full text-left bg-white rounded-2xl border p-4 transition-all hover:-translate-y-0.5 hover:shadow-md ${active?.case_id === c.case_id ? "border-cobalt ring-2 ring-cobalt/20" : "border-slate-100"}`}>
                  <div className="flex items-center justify-between">
                    <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${st.cls}`}>{st.label}</span>
                    <ChevronRight className="w-4 h-4 text-slate-300" />
                  </div>
                  <div className="mt-2 font-semibold text-navy text-sm">{c.client_name || "Client"}</div>
                  <div className="text-xs text-slate-500">{c.title}</div>
                  <div className="text-xs text-slate-400 mt-1 flex items-center gap-1"><Camera className="w-3 h-3" /> {c.evidence_count} files</div>
                </button>
              );
            })}
          </div>

          <div className="lg:col-span-2" id="attorney-case-detail">
            {!active ? (
              <div className="bg-white rounded-3xl border border-slate-100 p-12 text-center text-slate-400 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
                <FileText className="w-12 h-12 mx-auto text-slate-200" />
                <p className="mt-3">Select a referral to review the full case file.</p>
              </div>
            ) : (
              <div className="space-y-6">
                <div className="bg-white rounded-3xl border border-slate-100 p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                    <div>
                      <h2 className="font-heading text-xl font-bold text-navy">{active.title}</h2>
                      <p className="text-sm text-slate-500">{active.client_name} · {active.driver?.email || "—"}</p>
                    </div>
                    <a href={`${process.env.REACT_APP_BACKEND_URL}/api/cases/${active.case_id}/export?auth=${token}`} data-testid="attorney-download" className="flex items-center justify-center gap-2 px-5 py-2.5 rounded-full bg-navy text-white font-medium hover:bg-navy/90 transition-all"><FolderDown className="w-4 h-4" /> Download Case File</a>
                    </div>
                  {active.claimed_by === user?.user_id && (
                    <div className="mt-4">
                      <button onClick={() => navigate(`/intelligence/${active.case_id}`)} data-testid="open-intelligence" className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-cobalt text-white font-medium hover:bg-cobalt-hover transition-all active:scale-95"><Scale className="w-4 h-4" /> Open Case Intelligence (CRI / CFI / Evidence AI)</button>
                    </div>
                  )}
                  {active.referral_status === "requested" ? (
                    <div className="mt-5 flex gap-3">
                      <button onClick={() => act(active.case_id, "accepted")} data-testid="accept-case" className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-cobalt text-white font-medium hover:bg-cobalt-hover transition-all active:scale-95"><Check className="w-4 h-4" /> Accept Case</button>
                      <button onClick={() => act(active.case_id, "declined")} data-testid="decline-case" className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-white border border-slate-200 text-slate-700 font-medium hover:bg-slate-50 transition-all"><X className="w-4 h-4" /> Decline</button>
                    </div>
                  ) : (
                    <span className={`inline-block mt-4 text-xs font-semibold px-3 py-1 rounded-full ${STATUS[active.referral_status]?.cls}`}>{STATUS[active.referral_status]?.label}</span>
                  )}
                </div>

                <div className="bg-white rounded-3xl border border-slate-100 p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
                  <h3 className="font-heading font-semibold text-navy mb-4 flex items-center justify-between gap-2">
                    <span className="flex items-center gap-2"><Camera className="w-5 h-5 text-cobalt" /> Evidence ({active.evidence?.length || 0})</span>
                    <EvidenceIntelBadge caseId={active.case_id} />
                  </h3>
                  {active.evidence?.length ? (
                    <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                      {active.evidence.map((ev) => {
                        const url = `${process.env.REACT_APP_BACKEND_URL}/api/evidence/${ev.evidence_id}/download?auth=${token}`;
                        return (
                          <a key={ev.evidence_id} href={url} target="_blank" rel="noreferrer" className="relative rounded-xl overflow-hidden border border-slate-100 aspect-square bg-slate-50">
                            {ev.media_type === "image" ? <img src={url} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-slate-400 text-xs">{ev.media_type}</div>}
                            <span className="absolute bottom-0 inset-x-0 bg-black/50 text-white text-[10px] px-1 py-0.5 truncate">{ev.folder}</span>
                          </a>
                        );
                      })}
                    </div>
                  ) : <p className="text-sm text-slate-400">No evidence.</p>}
                </div>

                <div className="bg-white rounded-3xl border border-slate-100 p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
                  <h3 className="font-heading font-semibold text-navy mb-4 flex items-center gap-2"><Clock className="w-5 h-5 text-cobalt" /> Timeline</h3>
                  {active.timeline?.map((e, i) => (
                    <div key={e.event_id} className="flex gap-3">
                      <div className="flex flex-col items-center">
                        <div className="h-2.5 w-2.5 rounded-full bg-cobalt mt-1.5" />
                        {i < active.timeline.length - 1 && <div className="w-px flex-1 bg-slate-100 my-1" />}
                      </div>
                      <div className="pb-4">
                        <div className="text-sm font-medium text-navy">{e.title}</div>
                        <div className="text-xs text-slate-400">{new Date(e.occurred_at).toLocaleString()}</div>
                      </div>
                    </div>
                  ))}
                </div>

                {active.claimed_by === user?.user_id && (
                  <>
                    <div id="crm-panel">
                      {active.claimed_by === user?.user_id && <CaseDocSyncBanner caseId={active.case_id} audience="attorney" className="mb-4" />}
                      <CrmImportPanel caseId={active.case_id} />
                    </div>
                    <DocumentRequests caseId={active.case_id} isAttorney={true} />
                    <div id="messages-panel"><MessagesSection caseId={active.case_id} /></div>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
        </>)}
      </main>
      <CommandBar open={cmdOpen} onClose={() => setCmdOpen(false)} onNavigate={handleNav} />
      <QuickActions onNavigate={handleNav} />
      {showAutoAccept && <AutoAcceptPrefs onClose={() => setShowAutoAccept(false)} />}
      {showTelehealth && <AttorneyTelehealth onClose={() => setShowTelehealth(false)} />}
      <CrashReadyVoice />
    </div>
  );
}
